import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import DataTable from "@/components/DataTable";
import ImportButton from "@/components/ImportButton";
import { PageShell } from "@/components/SidebarLayout";
import { api, formatApiError } from "@/lib/api";
import { formatMoney } from "@/lib/format";

export default function LandSales() {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);

  const load = useCallback(async () => {
    const [r, p, sb] = await Promise.all([
      api.get("/land-sale-fees"),
      api.get("/projects"),
      api.get("/sales-bookings"),
    ]);
    const saleMap = {};
    for (const s of sb.data || []) saleMap[`${s.project_name}|${s.month}`] = s;
    setRows((r.data || []).map((row) => {
      const sale = saleMap[`${row.project_name}|${row.sale_month}`];
      return {
        ...row,
        sales_value: sale ? sale.sales_value : "",
        _sales_auto: sale?.source === "auto_fee",
        _fee_auto: row.source === "auto_15pct",
      };
    }));
    setProjects(p.data.filter((x) => x.type === "land"));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const projectOptions = projects.map((p) => ({ value: p.name, label: p.name, id: p.id }));

  const columns = [
    {
      key: "project_name",
      label: "Land Project",
      type: "select",
      options: projectOptions,
      format: (v) => v || "–",
    },
    { key: "sale_month", label: "Sale Month", type: "month" },
    {
      key: "sales_value",
      label: "Sales Value (₹L)",
      type: "money",
      align: "right",
      format: (v, row) => (
        v == null || v === ""
          ? <span className="text-muted-light">–</span>
          : <span className="num text-muted-dark" data-testid={`land-sales-value-${row.project_name}-${row.sale_month}`}>
              {formatMoney(v)}
              {row._sales_auto && <span className="ml-1 text-[9px] uppercase tracking-widest text-gold-600">auto</span>}
            </span>
      ),
    },
    {
      key: "development_fee",
      label: "Dev. Fee (₹L) · ≈15%",
      type: "money",
      align: "right",
      format: (v, row) => (
        <span className="num text-ink">
          {formatMoney(v)}
          {row._fee_auto && <span className="ml-1 text-[9px] uppercase tracking-widest text-gold-600">auto</span>}
        </span>
      ),
    },
    { key: "collection_lag_months", label: "Lag (m)", type: "number", align: "right" },
  ];

  const resolveIds = (draft) => {
    const proj = projects.find((p) => p.name === draft.project_name);
    return { ...draft, project_id: proj?.id || draft.project_id || "" };
  };

  const onCreate = async (draft) => {
    try {
      const body = resolveIds(draft);
      if (!body.project_id) return toast.error("Pick a land project");
      await api.post("/land-sale-fees", body);
      toast.success("Land fee added");
      await load();
    } catch (e) {
      toast.error("Add failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const onUpdate = async (id, draft) => {
    try {
      const body = resolveIds(draft);
      await api.put(`/land-sale-fees/${id}`, body);
      toast.success("Land fee updated");
      await load();
    } catch {
      toast.error("Update failed");
    }
  };

  const onDelete = async (id) => {
    try {
      await api.delete(`/land-sale-fees/${id}`);
      toast.success("Land fee deleted");
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <PageShell
      testId="land-sales-page"
      kicker="Plotted Development"
      title="Land Sales"
      actions={<ImportButton testId="land-import" onDone={load} />}
    >
      <p className="-mt-2 text-muted-dark text-sm max-w-2xl">
        Monthly development-fee ledger for land projects — the fee is ~<span className="font-semibold text-ink">15% of the plot sale value</span>
        (Sales Value column, from the Sales Bookings tab). 100% of each fee row is
        projected to collect at <span className="font-semibold text-ink">sale month + lag</span>.
        Editing Sales Value recalculates the Dev. Fee at 15%; editing the Dev. Fee updates Sales Bookings — the two stay in sync.
      </p>
      <DataTable
        module="land"
        columns={columns}
        rows={rows}
        onCreate={onCreate}
        onUpdate={onUpdate}
        onDelete={onDelete}
        primaryLabel="project_name"
        primaryMetric="development_fee"
      />
    </PageShell>
  );
}
